/**
 * Çevrimdışı yakalama — sayfası olmayan modül.
 *
 * Görevi: ağ yokken kaydedilen belgeyi cihazda tutmak, bağlantı gelince
 * sırayla göndermek. Sunucu tek doğru kaynak olarak kalır; cihaz yalnızca
 * bekleme odasıdır.
 *
 * Gönderimi bu modül YAPMAZ. Her kayıt türü kendi göndericisini kaydeder:
 *
 *   window.tekOffline.registerSender('document', async (payload) => { ... });
 *   window.tekOffline.enqueue('document', { ... });
 *
 * Böylece kuyruk hangi uç noktaya gidildiğini bilmez; yükleme yolu değişirse
 * kuyruk değişmez.
 *
 * KALDIRMAK İÇİN: bu klasörü sil ve shell/index.ts listesinden satırı çıkar.
 * Uygulamanın geri kalanı etkilenmez — enqueue çağrıları sessizce yoksayılır.
 */

import { defineFeature } from '../../shell/feature.ts';
import { legacy } from '../../shell/bridge.ts';
import * as store from './store.ts';
import {
  isDiscardable,
  newItem,
  onFailed,
  onSent,
  pendingCount,
  readyToSend,
  type QueueItem,
} from './queue.ts';

type Sender = (payload: unknown, item: QueueItem) => Promise<void>;

const senders = new Map<string, Sender>();
let flushing = false;
let listening = false;

/** Aktif profilin kimliği. Kuyruk bununla bölünür — yoksa gönderim yapılmaz. */
function currentProfileId(): string | null {
  const u = legacy.user as { sbProfileId?: string } | null;
  return u && typeof u.sbProfileId === 'string' && u.sbProfileId ? u.sbProfileId : null;
}

function online(): boolean {
  const n = (globalThis as { navigator?: { onLine?: boolean } }).navigator;
  return n?.onLine !== false;   // bilinmiyorsa çevrimiçi say, denemek serbest
}

/** Kayıt türü için gönderici tanımla. */
function registerSender(kind: string, fn: Sender): void {
  senders.set(kind, fn);
}

/**
 * Kabuk yüklenmeden önce kaydolmak isteyenleri topla.
 *
 * js/*.js dosyaları klasik script; AYRIŞTIRMA sırasında çalışırlar. Bu modül
 * ise ERTELENMİŞ bir modül — onlardan sonra çalışır. Yani eski kod
 * registerSender'ı çağırmak istediğinde fonksiyon henüz ortada yoktur.
 *
 * Zamanlamaya (DOMContentLoaded gibi) güvenmek kırılgan: kaydolamayan gönderici
 * HATA VERMEZ, kuyruk sessizce göndericisiz kalır ve belgeler sonsuza kadar
 * bekler. Bunun yerine eski kod bir diziye yazar, kabuk açılışta o diziyi
 * boşaltır. Sıra ne olursa olsun sonuç aynı.
 */
function drainPendingSenders(): void {
  const g = globalThis as unknown as { __tekOfflinePendingSenders?: Array<[string, Sender]> };
  const list = g.__tekOfflinePendingSenders;
  if (!Array.isArray(list)) return;
  for (const entry of list) {
    if (Array.isArray(entry) && entry.length === 2) registerSender(String(entry[0]), entry[1]);
  }
  list.length = 0;
}

/**
 * Kaydı kuyruğa al. Çevrimiçiysek hemen gönderilmeye çalışılır; değilsek
 * cihazda bekler. Her iki durumda da çağıran taraf aynı şeyi yapar.
 */
async function enqueue(kind: string, payload: unknown): Promise<boolean> {
  const pid = currentProfileId();
  if (!pid) return false;
  const ok = await store.put(newItem(kind, pid, payload));
  if (ok && online()) void flush();
  return ok;
}

/**
 * Bekleyenleri sırayla gönder.
 *
 * Tek seferde tek kayıt: belgeler çekildikleri sırayla gitsin ve bir hata
 * sonrakini etkilemesin. İlk ağ hatasında durur — sunucu ölüyse arkasına
 * yüzlerce istek yığmanın anlamı yok.
 */
async function flush(): Promise<void> {
  if (flushing) return;
  const pid = currentProfileId();
  if (!pid || !online()) return;
  flushing = true;
  try {
    const items = readyToSend(await store.all(), pid);
    for (const item of items) {
      const send = senders.get(item.kind);
      if (!send) continue;                       // gönderici yok — kayıt bekler
      try {
        await send(item.payload, item);
        const done = onSent(item);
        if (isDiscardable(done)) await store.remove(done.id);
        else await store.put(done);
      } catch (e) {
        await store.put(onFailed(item, String(e)));
        if (!online()) break;                    // ağ gitti — kalanı sonraya bırak
      }
    }
  } finally {
    flushing = false;
  }
}

/** Aktif profilde gönderilmeyi bekleyen kayıt sayısı. */
async function pending(): Promise<number> {
  const pid = currentProfileId();
  return pid ? pendingCount(await store.all(), pid) : 0;
}

export default defineFeature({
  id: 'offline',
  label: 'Çevrimdışı yakalama',
  view: null,

  boot: () => {
    if (listening) return;
    listening = true;
    /* Kabuktan önce çalışan eski kodun gönderici kayıtlarını al. */
    drainPendingSenders();
    /* Bağlantı geri geldiğinde kuyruk kendiliğinden boşalır. */
    globalThis.addEventListener?.('online', () => { void flush(); });
    /* Açılışta bir kez: önceki oturumdan kalanlar gönderilsin. */
    void flush();
  },

  /* js/*.js tarafının kapısı. Tek tek fonksiyon olarak veriliyor çünkü kabuk
     modül kaldırıldığında bu adları globalden SİLİYOR; tek bir nesne olarak
     verilseydi temizlik yapılamazdı. */
  legacyGlobals: {
    tekOfflineEnqueue: (kind: unknown, payload: unknown) => enqueue(String(kind), payload),
    tekOfflineFlush: () => flush(),
    tekOfflinePending: () => pending(),
    tekOfflineRegisterSender: (kind: unknown, fn: unknown) =>
      registerSender(String(kind), fn as Sender),
  },

  owns: { tables: [] },
});
